/**
 * Typed storage errors for structured error handling.
 */

export class StorageError extends Error {
  constructor(message: string, public readonly code: string) {
    super(message);
    this.name = 'StorageError';
  }
}

export class FileValidationError extends StorageError {
  constructor(message: string) {
    super(message, 'file_validation');
    this.name = 'FileValidationError';
  }
}

export class FileTooLargeError extends StorageError {
  constructor(sizeMB: number, maxMB: number) {
    super(`File size ${sizeMB.toFixed(1)}MB exceeds limit of ${maxMB}MB.`, 'file_too_large');
    this.name = 'FileTooLargeError';
  }
}

export class InvalidMimeTypeError extends StorageError {
  constructor(mimeType: string, allowed: string[]) {
    super(`MIME type "${mimeType}" is not allowed. Allowed: ${allowed.join(', ')}.`, 'invalid_mime_type');
    this.name = 'InvalidMimeTypeError';
  }
}

export class InvalidExtensionError extends StorageError {
  constructor(extension: string, allowed: string[]) {
    super(`Extension ".${extension}" is not allowed. Allowed: ${allowed.join(', ')}.`, 'invalid_extension');
    this.name = 'InvalidExtensionError';
  }
}

export class ImageDimensionError extends StorageError {
  constructor(width: number, height: number, maxWidth: number, maxHeight: number) {
    super(
      `Image dimensions ${width}x${height} exceed maximum ${maxWidth}x${maxHeight}.`,
      'image_dimension',
    );
    this.name = 'ImageDimensionError';
  }
}

export class StorageProviderUnavailableError extends StorageError {
  constructor(provider: string) {
    super(`Storage provider "${provider}" is unavailable.`, 'provider_unavailable');
    this.name = 'StorageProviderUnavailableError';
  }
}

export class FileNotFoundError extends StorageError {
  constructor(path: string) {
    super(`File not found: ${path}`, 'file_not_found');
    this.name = 'FileNotFoundError';
  }
}

export class UploadCancelledError extends StorageError {
  constructor() {
    super('Upload was cancelled.', 'upload_cancelled');
    this.name = 'UploadCancelledError';
  }
}

export class UploadFailedError extends StorageError {
  constructor(reason: string) {
    super(`Upload failed: ${reason}`, 'upload_failed');
    this.name = 'UploadFailedError';
  }
}

export function isStorageError(error: unknown): error is StorageError {
  return error instanceof StorageError;
}
